import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Shell } from "@/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { isInstalledPwa, readInloglink } from "@/features/session/inloglink";

export function InloggenPage() {
  const { t } = useTranslation("session");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [link] = useState(() => readInloglink(window.location.hash));
  const [failed, setFailed] = useState(link === null);
  // React runs an effect twice in development; a second consume would find the link spent.
  const presented = useRef(false);

  useEffect(() => {
    // Out of the address bar and the history, whatever happens next.
    window.history.replaceState(null, "", window.location.pathname);
    if (!link || presented.current) return;
    presented.current = true;

    void (async () => {
      try {
        const { response } = await api.POST("/api/auth/inloglink/consume", {
          body: { ...link, pwa: isInstalledPwa() },
        });
        if (response.status !== 204) {
          setFailed(true);
          return;
        }
        await queryClient.resetQueries();
        await navigate({ to: "/", replace: true });
      } catch {
        setFailed(true);
      }
    })();
  }, [link, navigate, queryClient]);

  return (
    <Shell>
      <Card>
        {failed ? (
          <>
            <CardHeader>
              <CardTitle>{t("landing.invalidTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>{t("landing.invalidBody")}</p>
              <Link to="/" className="jb-link">
                {t("landing.back")}
              </Link>
            </CardContent>
          </>
        ) : (
          <CardContent className="pt-6">
            <p role="status" className="text-sm text-muted-foreground">
              {t("landing.busy")}
            </p>
          </CardContent>
        )}
      </Card>
    </Shell>
  );
}
